import {
  getRefreshToken,
  setRefreshToken,
  setToken,
} from "./../../shared/plugins/http-service";
import {
  createAsyncThunk,
  createSlice,
  isAsyncThunkAction,
} from "@reduxjs/toolkit";
import { RootState } from "app.store";

import {
  ConfirmModel,
  GlobalValueState,
  ICaptcha,
  ToastModel,
  ToastType,
} from "./global-value.models";
import { signInAPI } from "states/account/account.api";
import {
  ILoginRequestModel,
  ILoginResponseModel,
} from "states/account/account.models";
import { getCaptchaAPI } from "./global-value.api";
import { fetchCustomer } from "states/customer/customer.slice";
interface ValidationErrors {
  errorMessage: string;
  field_errors: Record<string, string>;
}
export const loginRequest = createAsyncThunk<
  ILoginResponseModel,
  ILoginRequestModel,
  { rejectValue: ValidationErrors }
>("account/login", async (model, { rejectWithValue }) => {
  try {
    let response = (await signInAPI(model))?.data;
    setToken(response.accessToken);
    setRefreshToken(response.refreshToken);
    return await response;
  } catch (err) {
    return rejectWithValue(err?.response?.data?.error?.message);
  }
});

export const getCaptchaRequest = createAsyncThunk<
  ICaptcha,
  void,
  { rejectValue: ValidationErrors }
>("gcaptcha", async (thunkAPI, { rejectWithValue }) => {
  try {
    let response = (await getCaptchaAPI())?.data;
    return await response;
  } catch (err) {
    return rejectWithValue(err?.response?.data?.error?.message);
  }
});

export const globalValueSlice = createSlice({
  name: "globalValue",
  initialState: {
    isLoading: false,
    activeMenu: "",
    captcha: {} as ICaptcha,
    toast: {
      duration: 3000,
    } as ToastModel,
    confirm: {} as ConfirmModel,
    isSignIn:
      getRefreshToken() === undefined || getRefreshToken() === null
        ? false
        : true,
  } as GlobalValueState,
  reducers: {
    setLoading(state, action: { payload: boolean }) {
      state.isLoading = action.payload;
    },
    setToast(state, action: { payload: ToastModel }) {
      state.toast = action.payload;
    },
    setActiveMenu(state, action: { payload: string }) {
      state.activeMenu = action.payload;
    },
    setSignOut(state) {
      state.isSignIn = false;
    },
    showSuccess(state, action: { payload: string }) {
      state.toast = {
        ...state.toast,
        message: action.payload,
        type: ToastType.success,
        isShowToast: true,
      };
    },
    showError(state, action: { payload: string }) {
      state.toast = {
        ...state.toast,
        message: action.payload,
        type: ToastType.error,
        isShowToast: true,
      };
    },
    showWarning(state, action: { payload: string }) {
      state.toast = {
        ...state.toast,
        message: action.payload,
        type: ToastType.warn,
        isShowToast: true,
      };
    },
    showInfo(state, action: { payload: string }) {
      state.toast = {
        ...state.toast,
        message: action.payload,
        type: ToastType.info,
        isShowToast: true,
      };
    },
    showConfirmModal(state, action: { payload: ConfirmModel }) {
      state.confirm = {
        ...action.payload,
        isModal: true,
      };
    },
    closeConfirmModal(state) {
      state.confirm = {
        ...state.confirm,
        isModal: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isAsyncThunkAction(fetchCustomer), (state, action) => {
      state.isLoading = true;
      if (action.meta.requestStatus === "fulfilled") {
        state.isLoading = false;
      }
    });
    builder.addMatcher(isAsyncThunkAction(loginRequest), (state, action) => {
      if (action.meta.requestStatus === "fulfilled") {
        state.isSignIn = true;
      }
    });
    builder.addMatcher(
      isAsyncThunkAction(getCaptchaRequest),
      (state, action) => {
        if (action.meta.requestStatus === "fulfilled") {
          state.captcha = action.payload as ICaptcha;
        }
      }
    );
  },
});

export const {
  setLoading,
  setToast,
  showConfirmModal,
  closeConfirmModal,
  showError,
  showInfo,
  setActiveMenu,
  showSuccess,
  showWarning,
  setSignOut,
} = globalValueSlice.actions;

export const selectConfirmModal = (state: RootState) =>
  state.globalValue.confirm;
export const selectToast = (state: RootState) => state.globalValue.toast;
export const selectSignIn = (state: RootState) => state.globalValue.isSignIn;
export const selectActiveMenu = (state: RootState) =>
  state.globalValue.activeMenu;
export const selectCaptcha = (state: RootState) => state.globalValue.captcha;
